import { useEffect, useState } from 'react';
import { trackWarning, trackError } from '../utils/errorTracking';
import { 
  APP_VERSION, 
  CACHE_CONFIG, 
  DEV_CONFIG, 
  isVersionCompatible 
} from '../config/appVersion';

// Wrapper para localStorage que simula la API de AsyncStorage
const Storage = {
  async getItem(key) {
    try {
      return localStorage.getItem(key);
    } catch (error) {
      console.warn(`Error getting item ${key}:`, error);
      return null;
    }
  },

  async setItem(key, value) {
    try {
      localStorage.setItem(key, value);
    } catch (error) {
      console.warn(`Error setting item ${key}:`, error);
      throw error;
    }
  },

  async removeItem(key) {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.warn(`Error removing item ${key}:`, error);
      throw error;
    }
  },

  async getAllKeys() {
    try {
      return Object.keys(localStorage);
    } catch (error) {
      console.warn('Error getting all keys:', error);
      return [];
    }
  },

  async clear() {
    try {
      localStorage.clear();
    } catch (error) {
      console.warn('Error clearing storage:', error);
      throw error;
    }
  }
};

export const useCacheManager = () => {
  const [isClearing, setIsClearing] = useState(false);
  const [lastCleared, setLastCleared] = useState(null);
  const [currentVersion, setCurrentVersion] = useState(APP_VERSION);

  useEffect(() => {
    checkAndClearCacheIfNeeded();
  }, []);

  const checkAndClearCacheIfNeeded = async () => {
    try {
      // Obtener la versión guardada anteriormente
      const savedVersion = await Storage.getItem('app_version');
      
      // Verificar si la versión es compatible
      const shouldClearCache = !isVersionCompatible(savedVersion, APP_VERSION);
      
      if (shouldClearCache) {
        console.log(`🔄 Versión incompatible: ${savedVersion} → ${APP_VERSION}. Limpiando cache...`);
        
        // Trackear el cambio de versión
        trackWarning('App version incompatible, clearing cache', {
          oldVersion: savedVersion,
          newVersion: APP_VERSION,
          minCompatible: CACHE_CONFIG.MIN_COMPATIBLE_VERSION,
          timestamp: new Date().toISOString()
        });

        // Limpiar cache automáticamente
        await clearAllCache();
        
        // Guardar nueva versión
        await Storage.setItem('app_version', APP_VERSION);
        
        console.log('✅ Cache limpiado automáticamente por incompatibilidad de versión');
      } else {
        if (DEV_CONFIG.VERBOSE_LOGGING) {
          console.log('✅ Versión de app compatible, no es necesario limpiar cache');
        }
      }
    } catch (error) {
      console.error('❌ Error al verificar/limpiar cache:', error);
      trackError(error, {
        context: 'cache_manager',
        action: 'check_and_clear_cache',
        appVersion: APP_VERSION
      });
    }
  };

  const clearAllCache = async () => {
    setIsClearing(true);
    
    try {
      // Limpiar claves específicas
      for (const key of CACHE_CONFIG.STORAGE_KEYS_TO_CLEAR) {
        try {
          await Storage.removeItem(key);
          if (DEV_CONFIG.VERBOSE_LOGGING) {
            console.log(`🗑️ Limpiado: ${key}`);
          }
        } catch (error) {
          console.warn(`⚠️ No se pudo limpiar ${key}:`, error);
        }
      }

      // Limpiar claves por patrones
      const allKeys = await Storage.getAllKeys();
      const keysToRemove = allKeys.filter(key => {
        // No limpiar claves persistentes
        if (CACHE_CONFIG.PERSISTENT_KEYS.includes(key)) return false;
        
        // Limpiar claves que coincidan con patrones
        return CACHE_CONFIG.KEY_PATTERNS_TO_CLEAR.some(pattern => 
          key.includes(pattern.replace('_', ''))
        );
      });

      for (const key of keysToRemove) {
        try {
          await Storage.removeItem(key);
          if (DEV_CONFIG.VERBOSE_LOGGING) {
            console.log(`🗑️ Limpiado por patrón: ${key}`);
          }
        } catch (error) {
          console.warn(`⚠️ No se pudo limpiar ${key}:`, error);
        }
      }

      // En desarrollo, limpieza más agresiva si está configurado
      if (DEV_CONFIG.AGGRESSIVE_CLEANING) {
        const remainingKeys = await Storage.getAllKeys();
        const nonPersistentKeys = remainingKeys.filter(key => 
          !CACHE_CONFIG.PERSISTENT_KEYS.includes(key)
        );
        
        for (const key of nonPersistentKeys) {
          try {
            await Storage.removeItem(key);
            if (DEV_CONFIG.VERBOSE_LOGGING) {
              console.log(`🗑️ Limpieza agresiva: ${key}`);
            }
          } catch (error) {
            console.warn(`⚠️ No se pudo limpiar ${key}:`, error);
          }
        }
      }

      const timestamp = new Date().toISOString();
      setLastCleared(timestamp);
      
      // Trackear la limpieza exitosa
      trackWarning('Cache cleared successfully', {
        reason: 'version_incompatibility',
        oldVersion: await Storage.getItem('app_version'),
        newVersion: APP_VERSION,
        clearedKeys: [...CACHE_CONFIG.STORAGE_KEYS_TO_CLEAR, ...keysToRemove],
        aggressiveCleaning: DEV_CONFIG.AGGRESSIVE_CLEANING,
        timestamp
      });

      console.log('✅ Cache completamente limpiado');
      return true;
      
    } catch (error) {
      console.error('❌ Error al limpiar cache:', error);
      trackError(error, {
        context: 'cache_manager',
        action: 'clear_all_cache',
        appVersion: APP_VERSION
      });
      return false;
    } finally {
      setIsClearing(false);
    }
  };

  const clearCacheManually = async (reason = 'manual') => {
    if (!DEV_CONFIG.ALLOW_MANUAL_CLEAR) {
      console.warn('⚠️ Limpieza manual no permitida en esta configuración');
      return false;
    }

    try {
      const success = await clearAllCache();
      
      if (success) {
        // Trackear limpieza manual
        trackWarning('Cache cleared manually', {
          reason,
          appVersion: APP_VERSION,
          timestamp: new Date().toISOString()
        });
      }
      
      return success;
    } catch (error) {
      console.error('❌ Error en limpieza manual:', error);
      return false;
    }
  };

  const getCacheInfo = async () => {
    try {
      const allKeys = await Storage.getAllKeys();
      const cacheInfo = {};
      
      for (const key of allKeys) {
        try {
          const value = await Storage.getItem(key);
          cacheInfo[key] = {
            size: value ? value.length : 0,
            hasValue: !!value,
            isPersistent: CACHE_CONFIG.PERSISTENT_KEYS.includes(key),
            willBeCleared: CACHE_CONFIG.STORAGE_KEYS_TO_CLEAR.includes(key) ||
              CACHE_CONFIG.KEY_PATTERNS_TO_CLEAR.some(pattern => 
                key.includes(pattern.replace('_', ''))
              )
          };
        } catch (error) {
          cacheInfo[key] = { error: error.message };
        }
      }
      
      return {
        totalKeys: allKeys.length,
        keys: cacheInfo,
        appVersion: APP_VERSION,
        minCompatibleVersion: CACHE_CONFIG.MIN_COMPATIBLE_VERSION,
        lastCleared,
        config: {
          aggressiveCleaning: DEV_CONFIG.AGGRESSIVE_CLEANING,
          verboseLogging: DEV_CONFIG.VERBOSE_LOGGING,
          allowManualClear: DEV_CONFIG.ALLOW_MANUAL_CLEAR
        }
      };
    } catch (error) {
      console.error('❌ Error al obtener info del cache:', error);
      return null;
    }
  };

  const forceVersionUpdate = async (newVersion) => {
    try {
      await Storage.setItem('app_version', newVersion);
      setCurrentVersion(newVersion);
      console.log(`🔄 Versión forzada a: ${newVersion}`);
      
      // Limpiar cache después del cambio de versión
      await clearAllCache();
      
      return true;
    } catch (error) {
      console.error('❌ Error al forzar versión:', error);
      return false;
    }
  };

  return {
    // Estado
    isClearing,
    lastCleared,
    currentVersion,
    
    // Acciones
    clearAllCache,
    clearCacheManually,
    getCacheInfo,
    forceVersionUpdate,
    
    // Utilidades
    APP_VERSION,
    CACHE_CONFIG,
    DEV_CONFIG
  };
};

export default useCacheManager;
